import { useNavigate, useParams, Link, useLocation, NavLink } from 'react-router';
import {
  Maximize2, Minimize2, Bell, Settings, ArrowLeft, Sun, Moon, Monitor,
  User, LogOut, Menu, X, Lock, Check, Trash2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications, relativeTime } from '@/contexts/NotificationsContext';
import { useNavItems } from '@/hooks/useNavItems';
import { logout } from '@/api/auth';
import { useState, useEffect, Fragment } from 'react';

// ─── Alert dot colours ────────────────────────────────────────────────────────

const ALERT_DOT_COLORS = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  success: 'bg-emerald-500',
  error: 'bg-red-500',
} as const;

// ─── Topbar ───────────────────────────────────────────────────────────────────

export default function Topbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const { user, venues } = useAuth();
  const { notifications, unreadCount, markRead, clearAll } = useNotifications();
  const { visibleItems, isItemLocked } = useNavItems();
  const [profileOpen, setProfileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('dark');

  useEffect(() => {
    const stored = (localStorage.getItem('ghq-theme') || 'dark') as 'light' | 'dark' | 'system';
    setTheme(stored);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Listen to fullscreen changes (e.g. user presses Esc)
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Listen for OS theme changes when in system mode
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      document.documentElement.classList.toggle('dark', e.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const toggleTheme = () => {
    const cycle: Record<string, 'light' | 'dark' | 'system'> = { light: 'dark', dark: 'system', system: 'light' };
    const newTheme = cycle[theme] ?? 'dark';
    setTheme(newTheme);
    localStorage.setItem('ghq-theme', newTheme);
    // Cross-domain cookie so marketing site + other apps stay in sync
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host.startsWith('127.');
    const hParts = host.split('.');
    const rootDomain = hParts.length >= 2 ? `.${hParts.slice(-2).join('.')}` : '';
    const domainPart = !isLocal && rootDomain ? `; domain=${rootDomain}` : '';
    document.cookie = `ghq-theme=${newTheme}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${domainPart}`;
    // Apply to DOM
    if (newTheme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    } else {
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const handleClearAll = () => {
    clearAll();
    setAlertsOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.replace('/auth/login');
    } catch (error) {
    }
  };

  const currentVenue = venues.find(v => v.slug === slug) ?? venues[0];
  const venueSlug = currentVenue?.slug ?? slug ?? '';

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isOverview = pathSegments.includes('overview') || (pathSegments.length === 2 && pathSegments[0] === 'venues');

  let screenName = 'Dashboard';
  if (pathSegments.includes('settings')) screenName = 'Settings';
  if (pathSegments.includes('menu')) screenName = 'Menu Items';
  if (pathSegments.includes('integrations')) screenName = 'Integrations';
  if (pathSegments.includes('qr-codes')) screenName = 'QR Codes';
  if (pathSegments.includes('staff')) screenName = 'Staff';
  if (pathSegments.includes('billing')) screenName = 'Subscription';

  const planLabel = user?.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : 'Free';
  const roleLabel = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Owner';

  return (
    <>
      <header className="h-14 lg:h-16 card-premium !rounded-none !border-x-0 !border-t-0 flex items-center px-3 lg:px-4 gap-3 lg:gap-6 z-30 sticky top-0 flex-shrink-0 transition-colors duration-300">
        {/* ── Left: Hamburger + Logo + Screen Name ────────────────── */}
        <div className="flex items-center gap-2 lg:gap-3 flex-1 min-w-0">
          {/* Hamburger — mobile only */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden w-9 h-9 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl hover:bg-surface-muted transition-all active:scale-95 flex-shrink-0"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Venue logo — mobile only (dark logo for light theme) */}
          <div className="lg:hidden w-9 h-9 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center p-1.5 flex-shrink-0">
            <img
              src={(() => {
                if (theme === 'system') {
                  return document.documentElement.classList.contains('dark') ? '/logo-short.svg' : '/logo-short-dark.svg';
                }
                return theme === 'light' ? '/logo-short-dark.svg' : '/logo-short.svg';
              })()}
              alt="GatheredHQ"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Desktop: back button */}
          {!isOverview && (
            <button
              onClick={() => navigate(`/venues/${venueSlug}/overview`)}
              className="hidden lg:flex w-8 h-8 rounded-full border border-border items-center justify-center text-ink-muted hover:text-ink hover:bg-surface-muted transition-all active:scale-95 shadow-sm"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}

          <div className="flex items-center gap-2 font-black uppercase tracking-[2px] text-[11px] min-w-0">
            {/* Mobile: venue name */}
            <span className="lg:hidden text-ink truncate text-[12px] tracking-[1px] font-bold normal-case">
              {currentVenue?.name ?? 'Dashboard'}
            </span>
            {/* Desktop: screen name */}
            <span className="hidden lg:block text-ink">
              {screenName}
            </span>
          </div>
        </div>

        {/* ── Right: Utilities & User ─────────────────────────────── */}
        <div className="flex items-center gap-1 lg:gap-2">
          {/* Tool Section — desktop only */}
          <div className="hidden lg:flex items-center mr-3 border-r border-border pr-3 gap-1">
            {/* 1. Language */}
            {/* <button className="w-10 h-10 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl hover:bg-surface-muted transition-all" title="Language">
              <Languages className="w-5 h-5" />
            </button> */}
            {/* Country flag with region popup */}
            <div className="relative group">
              <button className="w-10 h-10 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl hover:bg-surface-muted transition-all">
                <img src="https://flagcdn.com/w20/ae.png" alt="UAE" className="w-5 h-auto opacity-80" />
              </button>
              <div className="absolute top-full right-0 mt-1 w-64 card-premium p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg">
                <div className="flex items-center gap-3 mb-2.5">
                  <img src="https://flagcdn.com/w40/ae.png" alt="UAE" className="w-8 h-auto rounded-sm shadow-sm" />
                  <div>
                    <p className="text-sm font-bold text-ink">United Arab Emirates</p>
                    <p className="text-[10px] text-ink-muted font-medium">AED · English</p>
                  </div>
                </div>
                <div className="border-t border-border pt-2.5">
                  <p className="text-[10px] text-ink-faint leading-relaxed">GatheredHQ is currently available in the UAE.</p>
                </div>
              </div>
            </div>
            {/* 2. Fullscreen toggle */}
            <button
              onClick={toggleFullscreen}
              className="w-10 h-10 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl hover:bg-surface-muted transition-all"
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            {/* 5. Alerts dropdown */}
            <div className="relative">
              <button
                onClick={() => { setAlertsOpen(o => !o); setProfileOpen(false); }}
                className="w-10 h-10 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl hover:bg-surface-muted transition-all relative"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-2 right-2 min-w-[16px] h-4 px-1 bg-orange-500 rounded-full border-2 border-surface text-[9px] font-black text-white flex items-center justify-center shadow-[0_0_8px_rgba(249,115,22,0.6)]">
                    {unreadCount}
                  </span>
                )}
              </button>

              {alertsOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAlertsOpen(false)} />
                  <div className="absolute top-full right-0 mt-3 w-80 bg-surface border border-border rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-border bg-surface-muted/50 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-ink">Notifications</p>
                        {unreadCount > 0 && (
                          <p className="text-[10px] text-ink-muted">{unreadCount} unread</p>
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <button
                          onClick={handleClearAll}
                          className="flex items-center gap-1.5 text-[10px] font-bold text-ink-muted hover:text-red-500 uppercase tracking-wider transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Clear all
                        </button>
                      )}
                    </div>

                    {/* Notification list */}
                    <div className="max-h-72 overflow-y-auto scrollbar-premium">
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center">
                          <Bell className="w-8 h-8 text-ink-faint mb-2" />
                          <p className="text-sm text-ink-muted font-medium">All caught up!</p>
                          <p className="text-xs text-ink-faint mt-0.5">No notifications right now</p>
                        </div>
                      ) : (
                        notifications.map(n => (
                          <button
                            key={n.id}
                            onClick={() => markRead(n.id)}
                            className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-surface-muted transition-colors flex items-start gap-3 ${
                              n.read ? 'opacity-60' : ''
                            }`}
                          >
                            <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${ALERT_DOT_COLORS[n.type]}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-sm truncate ${n.read ? 'font-medium text-ink-muted' : 'font-bold text-ink'}`}>
                                  {n.title}
                                </p>
                                {!n.read && <Check className="w-3 h-3 text-ink-faint shrink-0" />}
                              </div>
                              <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{n.message}</p>
                              <p className="text-[10px] text-ink-faint mt-1">{relativeTime(n.createdAt)}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* 3. Settings — navigates to /account/settings */}
            <button
              onClick={() => navigate('/account/settings')}
              className="w-10 h-10 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl hover:bg-surface-muted transition-all"
              title="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="w-10 h-10 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl hover:bg-surface-muted transition-all"
              title={theme === 'light' ? 'Switch to Dark' : theme === 'dark' ? 'Switch to System' : 'Switch to Light'}
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : theme === 'dark' ? <Monitor className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile: notification bell + theme toggle */}
          <div className="flex lg:hidden items-center gap-0.5">
            <div className="relative">
              <button
                onClick={() => { setAlertsOpen(o => !o); setProfileOpen(false); }}
                className="w-9 h-9 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl transition-all relative"
                title="Notifications"
              >
                <Bell className="w-[18px] h-[18px]" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[14px] h-3.5 px-0.5 bg-orange-500 rounded-full text-[8px] font-black text-white flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Mobile alerts dropdown */}
              {alertsOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAlertsOpen(false)} />
                  <div className="fixed left-3 right-3 top-16 bg-surface border border-border rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-3 border-b border-border bg-surface-muted/50 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-ink">Notifications</p>
                        {unreadCount > 0 && (
                          <p className="text-[10px] text-ink-muted">{unreadCount} unread</p>
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <button
                          onClick={handleClearAll}
                          className="flex items-center gap-1.5 text-[10px] font-bold text-ink-muted hover:text-red-500 uppercase tracking-wider transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="max-h-64 overflow-y-auto scrollbar-premium">
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <Bell className="w-7 h-7 text-ink-faint mb-2" />
                          <p className="text-sm text-ink-muted font-medium">All caught up!</p>
                        </div>
                      ) : (
                        notifications.map(n => (
                          <button
                            key={n.id}
                            onClick={() => markRead(n.id)}
                            className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-surface-muted transition-colors flex items-start gap-3 ${
                              n.read ? 'opacity-60' : ''
                            }`}
                          >
                            <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${ALERT_DOT_COLORS[n.type]}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${n.read ? 'font-medium text-ink-muted' : 'font-bold text-ink'}`}>
                                {n.title}
                              </p>
                              <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{n.message}</p>
                              <p className="text-[10px] text-ink-faint mt-1">{relativeTime(n.createdAt)}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={toggleTheme}
              className="w-9 h-9 flex items-center justify-center text-ink-muted hover:text-ink rounded-xl transition-all"
              title={theme === 'light' ? 'Switch to Dark' : theme === 'dark' ? 'Switch to System' : 'Switch to Light'}
            >
              {theme === 'light' ? <Moon className="w-[18px] h-[18px]" /> : theme === 'dark' ? <Monitor className="w-[18px] h-[18px]" /> : <Sun className="w-[18px] h-[18px]" />}
            </button>
          </div>

          {/* Subscription Status — desktop only */}
          <div className="hidden md:flex flex-col items-end mr-1 select-none">
            <span className="text-[9px] font-black uppercase tracking-[1.5px] text-orange-500 leading-none mb-0.5">
              {planLabel}
            </span>
            <span className="text-[10px] font-bold text-ink-muted leading-none lowercase tracking-tight">
              {roleLabel}
            </span>
          </div>

          {/* Profile Avatar & Popover */}
          <div className="relative">
            <button
              onClick={() => { setProfileOpen(!profileOpen); setAlertsOpen(false); }}
              className="w-9 h-9 lg:w-11 lg:h-11 rounded-xl bg-surface border border-border flex items-center justify-center overflow-hidden shadow-sm p-0.5 group hover:border-orange-500/50 hover:scale-105 transition-all active:scale-95"
            >
              <div className="w-full h-full rounded-[10px] bg-surface-muted flex items-center justify-center overflow-hidden border border-border relative">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt={user.fullName} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-orange-500/10 flex items-center justify-center select-none group-hover:scale-110 transition-transform duration-500">
                    <span className="text-orange-500 font-black text-[13px]">
                      {user?.fullName?.charAt(0).toUpperCase() ?? <User className="w-5 h-5" />}
                    </span>
                  </div>
                )}
              </div>
            </button>

            {profileOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setProfileOpen(false)}
                />
                <div className="absolute top-full right-0 mt-3 w-64 bg-surface border border-border rounded-xl shadow-2xl z-50 py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-4 py-3 border-b border-border bg-surface-muted/50">
                    <p className="text-[10px] text-ink-muted font-bold uppercase tracking-wider mb-1">Signed in as</p>
                    <p className="text-sm font-bold text-ink truncate">{user?.email}</p>
                  </div>

                  {/* 4. Account Settings — links to /account/settings */}
                  <Link
                    to="/account/settings"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-ink-muted hover:text-ink hover:bg-surface-muted transition-colors group"
                  >
                    <Settings className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                    <span className="text-sm font-medium">Account Settings</span>
                  </Link>

                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      void handleLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/5 transition-colors text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm font-medium">Sign out</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Mobile Drawer Overlay ──────────────────────────────────── */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setDrawerOpen(false)}
          />

          {/* Drawer — slides from left, theme-aware like sidebar */}
          <div className="absolute left-0 top-0 bottom-0 w-72 sidebar-premium flex flex-col animate-slide-in">
            {/* Drawer header */}
            <div className="flex items-center justify-between p-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center p-2 flex-shrink-0">
                  <img src="/logo-short-dark.svg" alt="GatheredHQ" className="w-full h-full object-contain dark:hidden" />
                  <img src="/logo-short.svg" alt="GatheredHQ" className="w-full h-full object-contain hidden dark:block" />
                </div>
                <div className="min-w-0">
                  <p className="font-black text-[14px] text-ink tracking-tight truncate leading-none mb-0.5">
                    {currentVenue?.name ?? 'My Venue'}
                  </p>
                  <p className="text-ink-faint text-[10px] uppercase tracking-[2px] font-black leading-none">
                    {planLabel} Plan
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-ink-muted hover:text-ink rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mx-4 divider-sidebar" />

            {/* Nav items — scrollable. Uses the same useNavItems source as the
                desktop sidebar so plan gates / permissions / owner-only filters
                stay in sync across viewports. */}
            <nav className="flex-1 overflow-y-auto scrollbar-premium px-3 py-2 space-y-0.5">
              {visibleItems.map((item) => {
                const locked = isItemLocked(item);
                // Hide tabs the current plan can't reach. Sidebar does the same thing a
                // different way (it iterates `activeItems`, which is already filtered),
                // and navParity.test.tsx pins the two together so they can't drift.
                if (locked) return null;
                // ⚠ Past that return, `locked` is always false — the padlock branch
                // below is UNREACHABLE. It's the old upgrade-teaser design, kept only
                // because re-enabling teasers is a live product option. Not evidence
                // the feature exists.
                return (
                  <Fragment key={item.to}>
                    {item.dividerBefore && <div className="mx-2 my-2 divider-sidebar" />}
                    {locked ? (
                      <div
                        title={item.locked ? 'Coming soon' : 'Upgrade your plan to unlock'}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-ink-faint/60 cursor-not-allowed select-none"
                      >
                        <div className="flex-shrink-0">{item.icon}</div>
                        <span className="text-[13px] font-medium flex-1 truncate">{item.label}</span>
                        <Lock className="w-3 h-3 text-ink-faint flex-shrink-0" />
                      </div>
                    ) : (
                      <NavLink
                        to={item.to}
                        onClick={() => setDrawerOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                            isActive
                              ? 'active-glow-vibrant text-ink dark:text-white shadow-sm dark:shadow-lg'
                              : 'text-ink-muted hover:bg-black/5 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white'
                          }`
                        }
                      >
                        <div className="flex-shrink-0">{item.icon}</div>
                        <span className="text-[13px] font-bold flex-1 truncate tracking-tight">{item.label}</span>
                        {!!item.badge && item.badge > 0 && (
                          <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </NavLink>
                    )}
                  </Fragment>
                );
              })}
            </nav>

            <div className="mx-4 divider-sidebar" />

            {/* Drawer footer — sign out */}
            <div className="p-3 flex-shrink-0">
              <button
                onClick={() => {
                  setDrawerOpen(false);
                  void handleLogout();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-[13px] font-bold">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
